import type { ComputedRef } from 'vue'
import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { Team } from '../interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useSidebarPrivileges } from '@entities/viewer'
import { useDayjs } from '@shared/lib/format'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'
import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'

import { getTeamEditPath } from './team-options'
import { useTeamsApi } from '../../api/teams/list.hook'
import { useTeamsStore } from './store'

interface TableTeamsReturn {
  teams: ComputedRef<Team[]>
  isLoading: ComputedRef<boolean>
  hasTeams: ComputedRef<boolean>
  canManage: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  perPageOptions: ComputedRef<number[]>
  formatDateTime: (value: string | null) => string
  openTeam: (team: Team) => Promise<void>
  askDelete: (team: Team) => void
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

export const useTableTeams = (): TableTeamsReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useTeamsStore()
  const dayjs = useDayjs()
  const { canManageTeams } = useSidebarPrivileges()

  const teams = computed<Team[]>(() => store.getTeams)
  const isLoading = computed<boolean>(() => store.isLoading)
  const hasTeams = computed<boolean>(() => store.hasTeams)
  const canManage = computed<boolean>(() => canManageTeams.value)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  const perPageOptions = computed<number[]>(() => {
    const current = meta.value.per_page
    if (!current || ROWS_PER_PAGE_OPTIONS.includes(current)) return ROWS_PER_PAGE_OPTIONS

    return [...ROWS_PER_PAGE_OPTIONS, current].sort((a, b) => a - b)
  })

  const formatDateTime = (value: string | null): string =>
    value ? dayjs(value).format(DateFormat.KanbanDate) : '—'

  const openTeam = async (team: Team): Promise<void> => {
    await router.push(getTeamEditPath(team.id))
  }

  const askDelete = (team: Team): void => {
    store.setTeamToDelete(team)
  }

  const setChangePage = async (value: number): Promise<void> => {
    store.setCurrentPage(value)
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
    await useTeamsApi()
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
    await useTeamsApi()
  }

  return {
    teams,
    isLoading,
    hasTeams,
    canManage,
    meta,
    perPageOptions,
    formatDateTime,
    openTeam,
    askDelete,
    setChangePage,
    setChangePerPage,
  }
}
